/**
 * Agent turn API client.
 *
 * One typed fetch wrapper around POST /api/agent/turn. Callable from
 * either side of the Next.js boundary:
 *
 *   - BROWSER (AgentSurface running in /compare's client tree):
 *     reads `NEXT_PUBLIC_API_URL`, no internal-auth header.
 *   - SERVER (compare/page.tsx Server Component running the first
 *     agent turn before render): reads `API_INTERNAL_URL`, includes
 *     `x-internal-auth` so the SearchSecurityMiddleware bypasses
 *     the per-IP rate limit.
 *
 * Stateless: caller passes the full message history each turn; the
 * backend has no session state.
 *
 * Day 6 (2026-06-15): client created.
 * Re-architecture 2026-06-16: server-side calling path added so the
 * agent surface lives on /compare (not a separate /agent route).
 */

import { getInternalAuthHeaders } from "./internal-headers";

export interface AgentTurnMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AgentTurnChip {
  label: string;
  search_query: string;
  warm_slug?: string | null;
}

export interface AgentTurnRequest {
  messages: AgentTurnMessage[];
  country_code?: string;
  postcode?: string;
}

export interface AgentTurnResult {
  action: "search" | "clarify";
  acknowledgment: string;
  message: string;
  chips: AgentTurnChip[];
  search_query: string | null;
  cost_usd: number;
  latency_ms: number;
  turn_id: string;
}

export class AgentTurnError extends Error {
  status: number;
  detail: unknown;

  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export const AGENT_TURN_STATUS_NETWORK = 0;
export const AGENT_TURN_STATUS_TIMEOUT = -1;

// Browser-side timeout. Backend turn is ~1.5 s; 30 s gives headroom
// for cold-start and the rare first-turn-after-deploy.
const AGENT_TURN_TIMEOUT_MS = 30_000;

// Server-side timeout. Has to cover the cold-start case where the
// LLM call + OpenAI network + Next.js fetch overhead can push a
// normally ~1.5 s call past 4 s. 12 s gives 4x headroom and matches
// the legacy clarifyQuery wait users already tolerate today.
// Anything above this falls back to the safe legacy clarify path
// rather than blocking /compare indefinitely.
const AGENT_TURN_SSR_TIMEOUT_MS = 12_000;

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return (
      process.env.API_INTERNAL_URL ??
      process.env.API_URL ??
      "http://localhost:8000"
    );
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export async function postAgentTurn(
  req: AgentTurnRequest,
): Promise<AgentTurnResult> {
  const url = `${apiBaseUrl()}/api/agent/turn`;
  const isServer = typeof window === "undefined";
  const timeoutMs = isServer
    ? AGENT_TURN_SSR_TIMEOUT_MS
    : AGENT_TURN_TIMEOUT_MS;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // getInternalAuthHeaders() returns {} on the browser so the
        // shared secret never leaks into a client bundle.
        ...getInternalAuthHeaders(),
      },
      body: JSON.stringify({
        messages: req.messages,
        country_code: req.country_code ?? "AU",
        postcode: req.postcode ?? "2000",
      }),
      cache: "no-store",
      signal: controller.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") {
      throw new AgentTurnError(
        AGENT_TURN_STATUS_TIMEOUT,
        null,
        `POST /api/agent/turn timed out after ${timeoutMs}ms`,
      );
    }
    throw new AgentTurnError(
      AGENT_TURN_STATUS_NETWORK,
      null,
      `POST /api/agent/turn network error: ${(e as Error).message}`,
    );
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let detail: unknown = null;
    try {
      detail = await response.json();
    } catch {
      detail = await response.text();
    }
    throw new AgentTurnError(
      response.status,
      detail,
      `POST /api/agent/turn returned ${response.status}`,
    );
  }
  return (await response.json()) as AgentTurnResult;
}
